import React, { useContext, useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import CardWithTitle from 'components/CardWithTitle';
import ButtonV2 from 'components/common/ButtonV2';
import DeleteConfirmationAlert from 'components/DeleteConfirmationAlert';
import { authContext } from 'contexts/AuthContext';
import { UPDATE_ORDER_STATUS } from 'pages/Orders/OrdersGraphQL';
import {
  generateOrderPi,
  generateOrderPo,
  saveGeneratedOrderDocumentUrl,
  uploadGeneratedOrderDocument,
} from 'pages/Orders/Addorders/orderService';

export const ACCEPT_OPERATION = 'ACCEPT';
export const REJECT_OPERATION = 'REJECT';
export const REJECTED_POLYSTOX_APPROVAL_IDS_KEY =
  'orders_rejected_polystox_approval_ids';

export const readRejectedPolystoxApprovalIds = () => {
  try {
    const rawValue = window.localStorage.getItem(
      REJECTED_POLYSTOX_APPROVAL_IDS_KEY,
    );
    if (!rawValue) return [];

    const parsedValue = JSON.parse(rawValue);
    if (!Array.isArray(parsedValue)) return [];

    return parsedValue.map(String);
  } catch (error) {
    return [];
  }
};

export const writeRejectedPolystoxApprovalIds = (ids) => {
  try {
    const uniqueIds = Array.from(new Set(ids.map(String)));
    window.localStorage.setItem(
      REJECTED_POLYSTOX_APPROVAL_IDS_KEY,
      JSON.stringify(uniqueIds),
    );
  } catch (error) {
    // Ignore localStorage failures.
  }
};

export const isOrderPolystoxApprovalRejected = (orderId) => {
  if (!orderId) return false;
  return readRejectedPolystoxApprovalIds().includes(String(orderId));
};

export const isFreightChargeAccepted = (order) =>
  order?.isFreightChargeAccepted === true ||
  order?.isFreightChargeAccepted === 1 ||
  order?.isFreightChargeAccepted === 'true';

export const isPolystoxApprovalWaiting = (order) => {
  if (!order || !order.id) return false;
  if (order.status !== 'PENDING') return false;
  if (
    ['ORDER_APPROVED', 'ORDER_REJECTED', 'ORDER_APPROVAL_EXPIRED'].includes(
      order.approvalStatus,
    )
  ) {
    return false;
  }
  if (isOrderPolystoxApprovalRejected(order.id)) return false;
  return isFreightChargeAccepted(order);
};

const PolystoxApproval = ({ order, onCompleted, onAccept, onReject }) => {
  const { auth } = useContext(authContext);
  const [updateOrderStatus] = useMutation(UPDATE_ORDER_STATUS);
  const [loadingOperation, setLoadingOperation] = useState(null);
  const [isRejected, setIsRejected] = useState(false);
  const [showRejectConfirmation, setShowRejectConfirmation] = useState(false);
  const [isApprovalHidden, setIsApprovalHidden] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setIsApprovalHidden(false);
    setIsRejected(
      order?.id
        ? readRejectedPolystoxApprovalIds().includes(String(order.id))
        : false,
    );
  }, [order?.id]);

  // Hide if order is not pending OR if approval status is rejected/approved/expired
  const shouldHideApproval =
    order?.status !== 'PENDING' ||
    ['ORDER_APPROVED', 'ORDER_REJECTED', 'ORDER_APPROVAL_EXPIRED'].includes(
      order?.approvalStatus
    ) ||
    isApprovalHidden;

  if (shouldHideApproval) {
    return null;
  }

  const refreshOrders = () => {
    window.dispatchEvent(new Event('orderCreated'));
    if (onCompleted) {
      onCompleted();
    }
  };

  const generateAndUploadDocument = async (documentType) => {
    const generator = documentType === 'PI' ? generateOrderPi : generateOrderPo;
    const generatedDocument = await generator(order?.id, {
      orderSequenceNumber: order?.orderSequenceNumber,
    });
    const uploadedUrl = await uploadGeneratedOrderDocument(
      generatedDocument?.fileSource,
      generatedDocument?.fileName,
      auth,
    );

    await saveGeneratedOrderDocumentUrl(
      order?.id,
      documentType,
      uploadedUrl,
      auth,
    );
  };

  const handleAccept = async () => {
    if (!order?.id) {
      toast.error('Order not available');
      return;
    }

    if (!auth?.data?.token) {
      toast.error('Authorization token not available');
      return;
    }

    try {
      setLoadingOperation(ACCEPT_OPERATION);

      const response = await updateOrderStatus({
        variables: {
          id: order?.id,
          status: 'IN_PROGRESS',
        },
      });

      if (response?.errors?.length) {
        throw new Error(response.errors[0].message);
      }

      const statusMessage =
        response?.data?.updateOrderStatus ||
        'Order has been IN PROGRESS successfully.';

      setIsApprovalHidden(true);
      if (onAccept) {
        onAccept();
      }
      toast.dismiss();
      toast.success(statusMessage);
      refreshOrders();

      const documentResults = await Promise.allSettled([
        generateAndUploadDocument('PI'),
        generateAndUploadDocument('PO'),
      ]);

      documentResults
        .filter((result) => result.status === 'rejected')
        .forEach((result) => {
          // eslint-disable-next-line no-console
          console.error(
            'Automatic PI/PO generation/upload failed',
            result.reason,
          );
        });
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Something went wrong.',
      );
      refreshOrders();
    } finally {
      if (isMountedRef.current) {
        setLoadingOperation(null);
      }
    }
  };

  const handleRejectConfirmation = () => {
    if (!order?.id) {
      toast.error('Order not available');
      return;
    }

    const currentOrderId = String(order.id);
    const existingIds = readRejectedPolystoxApprovalIds();
    writeRejectedPolystoxApprovalIds([...existingIds, currentOrderId]);
    setIsRejected(true);
    setShowRejectConfirmation(false);
    if (onReject) {
      onReject();
    }
    refreshOrders();
  };

  const isFreightAccepted = isFreightChargeAccepted(order);
  const isActionDisabled =
    loadingOperation !== null || isRejected || !isFreightAccepted;

  return (
    <>
      <CardWithTitle title="Waiting for Polystox approval(Polystox Approval)" className="mt-3">
        <div className="border rounded-8 p-3">
          <div className="d-flex align-items-start gap-2">
            <div
              className="d-flex align-items-start gap-2 flex-grow-1"
              style={{ minWidth: 0 }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  marginTop: 6,
                  backgroundColor: isRejected ? '#c0392b' : '#f39c12',
                  flexShrink: 0,
                }}
              />
              <div className="d-flex flex-column gap-1">
                <div className="fw-semibold">
                  {isRejected
                    ? 'Polystox approval rejected'
                    : 'Waiting for Polystox approval'}
                </div>
                <div className="f-12 text-gray mt-1">
                  {isRejected
                    ? 'This approval action has been rejected.'
                    : !isFreightAccepted
                      ? 'Waiting for freight charges updation.'
                      : 'Accepting will move this order to In Progress.'}
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap mt-3 ms-4">
            <ButtonV2
              type="button"
              size="sm"
              color="danger"
              className="border-radius-4 px-3"
              disabled={isActionDisabled}
              onClick={() => setShowRejectConfirmation(true)}
            >
              Reject
            </ButtonV2>
            <ButtonV2
              type="button"
              size="sm"
              color="success"
              className="border-radius-4 px-3"
              disabled={isActionDisabled}
              onClick={handleAccept}
            >
              {loadingOperation === ACCEPT_OPERATION
                ? 'Approving...'
                : 'Accept'}
            </ButtonV2>
          </div>
        </div>
      </CardWithTitle>

      <DeleteConfirmationAlert
        open={showRejectConfirmation}
        toggleAlert={() => setShowRejectConfirmation(false)}
        onCancel={() => setShowRejectConfirmation(false)}
        onConfirm={handleRejectConfirmation}
        message="Are you sure you want to reject Polystox approval?"
        addButtonLabel="Confirm"
        confirmButtonColor="danger"
        loading={loadingOperation === REJECT_OPERATION}
      />
    </>
  );
};

export default PolystoxApproval;
